import { pageNodes, Scene } from '@flobi/artwork-core';
import { TileRenderer, warmUpRenderer, type Camera, type FrameStats } from '@flobi/artwork-core/render';
import { useEffect, useRef, useState } from 'react';
import { fitCamera, panBy, zoomAt } from '../canvas/camera';
import { GlSurface, loadCanvasKit } from '../canvas/canvaskit';
import { SCENARIOS, scenarioStep, summarize, type FrameSample, type MeasureResult, type Scenario } from './scenarios';
import { buildStressPage } from './stressScene';
import { PALETTES, rgba, storedChoice, systemTheme } from '../theme';

const COUNTS = [1_000, 10_000, 50_000, 100_000];

interface LabInfo {
  count: number;
  warmUpMs: number;
  artboards: number;
  buildMs: number;
  sceneMs: number;
  rendererMs: number;
  gpu: string;
}

/** What the benchmark script drives through the page. */
export interface LabApi {
  info(): LabInfo & { units: number };
  settle(timeoutMs?: number): Promise<number>;
  measure(scenario: Scenario, durationMs: number): Promise<MeasureResult>;
}

declare global {
  interface Window {
    __artworkLab?: LabApi;
  }
}

const initialCount = () => {
  const n = Number(new URLSearchParams(window.location.search).get('n'));
  return COUNTS.includes(n) ? n : 10_000;
};

/**
 * The rendering spike: a generated file with up to 100,000 objects, drawn by the engine's
 * tile renderer. Scroll to pan, pinch or ⌘-scroll to zoom, 0 to fit.
 */
export function RenderLab() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const statsRef = useRef<HTMLPreElement>(null);
  const [count, setCount] = useState(initialCount);
  const [status, setStatus] = useState('Loading CanvasKit…');
  const [results, setResults] = useState<MeasureResult[]>([]);
  const [running, setRunning] = useState<Scenario | null>(null);
  const apiRef = useRef<LabApi | null>(null);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('n', String(count));
    window.history.replaceState(null, '', url);

    let disposed = false;
    let raf = 0;
    const cleanups: (() => void)[] = [];

    void (async () => {
      const ck = await loadCanvasKit();
      if (disposed) return;
      const canvas = canvasRef.current!;
      const gl = new GlSurface(ck, canvas);
      cleanups.push(() => gl.dispose());

      setStatus(`Building ${count.toLocaleString()} objects…`);
      await new Promise((r) => setTimeout(r, 30));
      if (disposed) return;
      let t = performance.now();
      const stress = buildStressPage(count);
      const buildMs = performance.now() - t;
      t = performance.now();
      const scene = new Scene(stress.page);
      const sceneMs = performance.now() - t;
      let needsFrame = true;
      t = performance.now();
      const choice = storedChoice();
      const canvasColour = rgba(PALETTES[choice === 'system' ? systemTheme() : choice].canvas);
      const renderer = new TileRenderer(ck, scene, { onInvalidate: () => (needsFrame = true), background: canvasColour });
      const rendererMs = performance.now() - t;
      cleanups.push(() => {
        renderer.dispose();
        scene.dispose();
      });
      gl.onRestored = () => {
        renderer.dropTiles();
        needsFrame = true;
      };

      const debug = document.createElement('canvas').getContext('webgl2');
      const ext = debug?.getExtension('WEBGL_debug_renderer_info');
      const gpu = debug ? String(debug.getParameter(ext ? ext.UNMASKED_RENDERER_WEBGL : debug.RENDERER)) : 'unknown';
      const warm = new URLSearchParams(window.location.search).get('warmup') !== '0';
      setStatus('Preparing the GPU…');
      const dpr = window.devicePixelRatio || 1;
      const warmUpMs = warm
        ? await warmUpRenderer(ck, gl.surfaceFor(canvas.clientWidth, canvas.clientHeight, dpr), {
            dpr,
            yieldFrame: () => new Promise((resolve) => requestAnimationFrame(() => resolve())),
          })
        : 0;
      if (disposed) return;
      const info: LabInfo = { count, warmUpMs, artboards: stress.artboardIds.length, buildMs, sceneMs, rendererMs, gpu };

      const viewSize = () => ({ width: canvas.clientWidth, height: canvas.clientHeight });
      let camera: Camera = fitCamera(stress.bounds, viewSize().width, viewSize().height);
      let lastStats: FrameStats | null = null;
      let driver: ((now: number) => void) | null = null;
      let lastSize = '';
      let lastDrawMs = 0;
      const frameWaiters = new Set<(stats: FrameStats, now: number) => void>();

      const frame = (now: number) => {
        const { width, height } = viewSize();
        const dpr = window.devicePixelRatio || 1;
        const sizeKey = `${width}x${height}@${dpr}`;
        if (sizeKey !== lastSize) {
          lastSize = sizeKey;
          needsFrame = true;
        }
        driver?.(now);
        if ((needsFrame || driver) && !gl.lost) {
          const surface = gl.surfaceFor(width, height, dpr);
          const before = performance.now();
          const stats = renderer.draw(surface, camera, { width, height, dpr });
          lastDrawMs = performance.now() - before;
          needsFrame = stats.pendingTiles > 0;
          lastStats = stats;
          for (const waiter of frameWaiters) waiter(stats, now);
        }
        raf = requestAnimationFrame(frame);
      };
      raf = requestAnimationFrame(frame);

      const setCamera = (next: Camera) => {
        camera = next;
        needsFrame = true;
      };
      const onWheel = (e: WheelEvent) => {
        e.preventDefault();
        if (driver) return;
        if (e.ctrlKey || e.metaKey) setCamera(zoomAt(camera, e.offsetX, e.offsetY, Math.exp(-e.deltaY * 0.01)));
        else setCamera(panBy(camera, -e.deltaX, -e.deltaY));
      };
      const onPointerMove = (e: PointerEvent) => {
        if (e.buttons === 0 || driver) return;
        setCamera(panBy(camera, e.movementX, e.movementY));
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === '0') setCamera(fitCamera(stress.bounds, viewSize().width, viewSize().height));
        if (e.key === '1') {
          const { width, height } = viewSize();
          setCamera({ x: camera.x + width / 2 / camera.zoom - width / 2, y: camera.y + height / 2 / camera.zoom - height / 2, zoom: 1 });
        }
      };
      canvas.addEventListener('wheel', onWheel, { passive: false });
      canvas.addEventListener('pointermove', onPointerMove);
      window.addEventListener('keydown', onKey);
      cleanups.push(() => {
        canvas.removeEventListener('wheel', onWheel);
        canvas.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('keydown', onKey);
      });

      // The object dragged in the drag scenario: one near the middle of the first artboard.
      const nodes = pageNodes(stress.page);
      const dragId = stress.objectIds[Math.floor(Math.min(stress.objectIds.length, 1000) / 2)]!;
      const dragStart = scene.worldTransform(dragId);
      const firstBoard = scene.worldTransform(stress.artboardIds[0]!);

      const api: LabApi = {
        info: () => ({ ...info, units: renderer.unitCount }),
        settle: (timeoutMs = 60_000) =>
          new Promise((resolve, reject) => {
            const started = performance.now();
            needsFrame = true;
            const waiter = (stats: FrameStats, now: number) => {
              if (stats.pendingTiles === 0) {
                frameWaiters.delete(waiter);
                resolve(Math.round(now - started));
              } else if (now - started > timeoutMs) {
                frameWaiters.delete(waiter);
                reject(new Error(`still ${stats.pendingTiles} tiles pending after ${timeoutMs} ms`));
              }
            };
            frameWaiters.add(waiter);
          }),
        measure: (scenario, durationMs) =>
          new Promise((resolve) => {
            const home = camera;
            const view = viewSize();
            const step = scenarioStep(scenario, {
              bounds: stress.bounds,
              view,
              durationMs,
              dragOrigin: { x: firstBoard[4] + 540, y: firstBoard[5] + 540 },
              moveObject: (x, y) => nodes.get(dragId)?.set('transform', [1, 0, 0, 1, x - firstBoard[4], y - firstBoard[5]]),
            });
            const samples: FrameSample[] = [];
            let started = -1;
            let last = 0;
            driver = (now) => {
              if (started < 0) {
                started = now;
                last = now;
              }
              camera = step(now - started);
            };
            const waiter = (stats: FrameStats, now: number) => {
              if (now !== started) samples.push({
                  interval: now - last,
                  drawMs: lastDrawMs,
                  drawnUnits: stats.drawnUnits,
                  renderMs: stats.renderMs,
                  pendingTiles: stats.pendingTiles,
                  renderedTiles: stats.renderedTiles,
                  level: stats.level,
                  at: Math.round(now - started),
                });
              last = now;
              if (now - started >= durationMs) {
                frameWaiters.delete(waiter);
                driver = null;
                if (scenario === 'drag') nodes.get(dragId)?.set('transform', dragStart);
                setCamera(home);
                resolve(summarize(scenario, samples, now - started));
              }
            };
            frameWaiters.add(waiter);
          }),
      };
      apiRef.current = api;
      window.__artworkLab = api;
      cleanups.push(() => {
        if (window.__artworkLab === api) delete window.__artworkLab;
        apiRef.current = null;
      });

      setStatus('');
      const tick = window.setInterval(() => {
        const s = lastStats;
        if (!statsRef.current || !s) return;
        statsRef.current.textContent = [
          `${info.count.toLocaleString()} objects on ${info.artboards} artboards`,
          `built ${Math.round(info.buildMs)} ms · scene ${Math.round(info.sceneMs)} ms · index ${Math.round(info.rendererMs)} ms · GPU warm-up ${Math.round(info.warmUpMs)} ms`,
          `zoom ${(camera.zoom * 100).toFixed(1)}% · level ${s.level}`,
          `tiles ${s.visibleTiles} visible · ${s.pendingTiles} pending · ${s.cachedTiles} cached (${Math.round(s.cachedBytes / 1048576)} MB)`,
          `last frame drew ${s.renderedTiles} tiles in ${s.renderMs.toFixed(1)} ms`,
          `GPU: ${info.gpu}`,
        ].join('\n');
      }, 250);
      cleanups.push(() => window.clearInterval(tick));
    })().catch((err: unknown) => setStatus(`Couldn't start: ${(err as Error).message}`));

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      for (const cleanup of cleanups.reverse()) cleanup();
    };
  }, [count]);

  const run = async (scenario: Scenario) => {
    const api = apiRef.current;
    if (!api || running) return;
    setRunning(scenario);
    await api.settle().catch(() => 0);
    const result = await api.measure(scenario, 5000);
    setResults((r) => [result, ...r].slice(0, 12));
    setRunning(null);
  };

  return (
    <div className="fixed inset-0 overflow-hidden bg-canvas text-ink">
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full touch-none" />
      <div className="absolute left-4 top-4 w-[23rem] max-w-[calc(100vw-2rem)] border border-line bg-surface/95 p-3 text-xs backdrop-blur">
        <div className="mb-2 flex items-baseline justify-between">
          <h1 className="label text-ink">Rendering lab</h1>
          <span className="label text-ink-muted">scroll · ⌘-scroll · 0</span>
        </div>
        <div className="mb-2 flex gap-1" role="group" aria-label="Objects">
          {COUNTS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setCount(n)}
              className={`figure flex-1 px-2 py-1 ${n === count ? 'bg-accent text-accent-ink' : 'border border-line text-ink-muted hover:text-ink'}`}
            >
              {n >= 1000 ? `${n / 1000}k` : n}
            </button>
          ))}
        </div>
        {status ? <p className="py-2 text-ink-muted">{status}</p> : <pre ref={statsRef} className="whitespace-pre-wrap font-mono leading-5 text-ink-muted" />}
        <div className="mt-2 grid grid-cols-2 gap-1">
          {(Object.keys(SCENARIOS) as Scenario[]).map((s) => (
            <button
              key={s}
              type="button"
              disabled={!!status || !!running}
              title={SCENARIOS[s]}
              onClick={() => void run(s)}
              className="label border border-line px-2 py-1 text-left text-ink-muted hover:text-ink disabled:opacity-50"
            >
              {running === s ? 'Measuring…' : `Measure ${s}`}
            </button>
          ))}
        </div>
        {results.length > 0 && (
          <table className="figure mt-2 w-full text-[11px]">
            <thead className="text-ink-muted">
              <tr>
                <th className="text-left font-normal">Scenario</th>
                <th className="text-right font-normal">fps</th>
                <th className="text-right font-normal">median</th>
                <th className="text-right font-normal">p95</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={i}>
                  <td>{r.scenario}</td>
                  <td className="text-right">{r.fps}</td>
                  <td className="text-right">{r.medianMs} ms</td>
                  <td className="text-right">{r.p95Ms} ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
